/**
 * Structured per-fabric copy for the PDP "Fabric" detail card.
 *
 * Source: pulled 2026-05-13 from the legacy Shopify storefront at
 * shop.diolo.com/collections/eql<fabric> — that's where the
 * merchant authored the original fabric descriptions back in 2024
 * (Cloud, Rib, Lux, Smooth, Seamless). Each fabric has the same
 * shape so the PDP card can render them uniformly:
 *
 *   - `name`     — Trademark name shown as the section heading
 *   - `tagline`  — One-line summary that sits under the name
 *   - `bullets`  — 2-4 concrete properties (velvet/airflow/etc.)
 *   - `bestFor`  — Intended use cases (intensity + activity)
 *
 * Vietnamese copy is best-effort author-friendly — merchant should
 * sweep the file once and tighten any phrasing they'd word differently.
 * Drop English bullets if a fabric ever ships in VN-only.
 *
 * Component consumer: `<FabricDetails fabric={product.fabric}
 * locale={locale} />` on the PDP, just below FabricFeatures icons.
 * Returns null when `fabric` is missing or doesn't map — keeps the
 * card off accessories (visor / socks / tumbler / tote) where it
 * would be irrelevant.
 */

export interface FabricInfo {
  name: string;
  taglineEn: string;
  taglineVi: string;
  bulletsEn: string[];
  bulletsVi: string[];
  bestForEn: string;
  bestForVi: string;
}

export const FABRIC_INFO: Record<string, FabricInfo> = {
  cloud: {
    name: "EQLCloud™",
    taglineEn: "A silky smooth and butter-like fabric",
    taglineVi: "Vải mịn mượt như lụa, mềm như bơ",
    bulletsEn: [
      "Velvety and luxuriously smooth",
      "Breathable, moisture-wicking material",
      "Sculpting and flattering — confidence and empowerment",
    ],
    bulletsVi: [
      "Cảm giác nhung mượt, sang trọng",
      "Thoáng khí và thấm hút mồ hôi tốt",
      "Ôm dáng tôn người — mặc tự tin, đầy năng lượng",
    ],
    bestForEn: "Low-to-high intensity training and everyday comfort",
    bestForVi: "Tập luyện cường độ thấp đến cao và mặc hằng ngày",
  },
  rib: {
    name: "EQLRib™",
    taglineEn: "A contemporary touch with ribbed-texture fabric",
    taglineVi: "Chất vải tạo cảm giác hiện đại nhờ kết cấu gân nhẹ",
    bulletsEn: [
      "Soft, airy fabric for cloud-like comfort",
      "Ribbed texture creates small gaps for airflow",
      "Functional yet stylish and modern aesthetic",
    ],
    bulletsVi: [
      "Vải mềm, thoáng, mang lại cảm giác nhẹ tênh",
      "Kết cấu gân giúp thoáng khí và mát da",
      "Vừa tiện dụng, vừa thời thượng và hiện đại",
    ],
    bestForEn: "Yoga, Pilates, low-to-medium intensity training, and everyday comfort",
    bestForVi: "Yoga, Pilates, tập luyện cường độ thấp–trung và mặc hằng ngày",
  },
  lux: {
    name: "EQLLux™",
    taglineEn: "Luxuriously soft, hug-like compression",
    taglineVi: "Mềm cao cấp, ôm sát tựa cái ôm",
    bulletsEn: [
      "Super soft & comfortably compressive — like wearing a hug",
      "Thick, breathable fabric with a cottony feel",
      "Wrinkle-resistant — built for enduring quality",
    ],
    bulletsVi: [
      "Siêu mềm và ôm vừa vặn — cảm giác như được ôm trọn",
      "Dày dặn, thoáng khí, chạm vào như cotton",
      "Chống nhăn — bền theo thời gian",
    ],
    bestForEn: "Medium-to-high intensity training, yoga, runs, hikes, walks, and everyday comfort",
    bestForVi: "Tập luyện cường độ trung–cao, yoga, chạy bộ, đi bộ và mặc hằng ngày",
  },
  smooth: {
    name: "EQLSmooth™",
    taglineEn: "Lightweight and sleek with a cool-touch feel",
    taglineVi: "Nhẹ tênh, mượt tay, chạm vào mát lạnh",
    bulletsEn: [
      "High-compression fabric for maximum support",
      "Sleek & sculpting with subtle sheen",
      "Cool-touch finish — silky feel with slight shine",
    ],
    bulletsVi: [
      "Vải ôm chặt mang lại nâng đỡ tối đa",
      "Bề mặt mượt, ôm dáng, ánh nhẹ tinh tế",
      "Chạm mát, mượt như lụa với độ bóng nhẹ",
    ],
    bestForEn: "Low-to-high intensity training, Pilates, yoga, and studio-to-street style",
    bestForVi: "Tập luyện cường độ thấp–cao, Pilates, yoga và phong cách từ phòng tập ra phố",
  },
  seamless: {
    name: "EQLSeamless™",
    taglineEn: "Seamless, woven, and airy for unrestricted movement",
    taglineVi: "Liền mạch, dệt thoáng, thoải mái mọi chuyển động",
    bulletsEn: [
      "Smooth & seam-free finish",
      "Moisture-wicking and very durable — built to last",
      "Very compressive — good support with a flattering fit",
    ],
    bulletsVi: [
      "Hoàn thiện liền mạch, không đường may",
      "Thấm hút mồ hôi tốt và bền chắc — mặc lâu vẫn đẹp",
      "Ôm sát body — nâng đỡ tốt, tôn dáng",
    ],
    bestForEn: "Medium-to-high intensity training and everyday comfort",
    bestForVi: "Tập luyện cường độ trung–cao và mặc hằng ngày",
  },
};

/**
 * Lookup helper. Tolerates case + trailing TM symbols ("EQLCloud™" /
 * "Cloud" / "cloud" all resolve to the same entry).
 */
export function getFabricInfo(fabric: string | null | undefined): FabricInfo | null {
  if (!fabric) return null;
  const key = fabric
    .toLowerCase()
    .replace(/[™®]/g, "")
    .replace(/^eql/, "")
    .trim();
  return FABRIC_INFO[key] || null;
}
